import {NotificationTypes} from '@/enums/notifier/NotificationTypes';
import {NotificationLike} from '@/types/NotificationLike.type';

/** Pop-ups of these types stay until they are handled; all others hide after {@link POPUP_AUTO_HIDE_MS} */
export const STICKY_POPUP_TYPES: string[] = [NotificationTypes.CALL, NotificationTypes.MENTION, NotificationTypes.OVERDUE_TO_DO, NotificationTypes.OVERDUE_TO_DO_DIGEST,
    NotificationTypes.DUE_TO_DO];

export const POPUP_AUTO_HIDE_MS = 8000;

/** Repeated notifications of these types are shown as one row in the panel ("14× überfällig gemeldet") */
export const REPEATED_GROUP_TYPES: string[] = [NotificationTypes.OVERDUE_TO_DO, NotificationTypes.DUE_TO_DO];

/** To-do notifications that offer Erledigen / Später erinnern */
export const TO_DO_ACTION_TYPES: string[] = [NotificationTypes.OVERDUE_TO_DO, NotificationTypes.DUE_TO_DO, NotificationTypes.TO_DO_ASSIGNED];

const payloadOf = (notification: NotificationLike): Record<string, unknown> => {
    const payload = (notification as { messagePayload?: unknown }).messagePayload;
    return payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
};

/**
 * Key of notifications that belong together: `groupKey` of newer notifiers, otherwise derived like the notifier does
 * (`<type>:<toDoId>`, `mention:<logEntryId>` …). Calls are never grouped.
 */
export function notificationGroupKey(notification: NotificationLike): string | undefined {
    if (notification.groupKey) {
        return notification.groupKey;
    }
    if (notification.type === NotificationTypes.CALL) {
        return undefined;
    }
    const payload = payloadOf(notification);
    // Takeover offers: one group per offer and event (an acceptance does not replace the pop-up of a decline)
    if (notification.type === NotificationTypes.TAKEOVER_OFFER_UPDATE && payload.takeoverOfferId) {
        return `${notification.type}:${String(payload.takeoverOfferId)}:${String(payload.event || '')}`;
    }
    const logEntry = (notification as { logEntry?: unknown }).logEntry;
    const ref = payload.toDoId || logEntry || payload.entityId || payload.handoutEventId || payload.configurationId
        || payload.substitutionInquiryId;
    return ref ? `${notification.type}:${String(ref)}` : undefined;
}

export function popupAutoHideMs(notification: NotificationLike): number {
    if (notification.timeout) {
        return notification.timeout;
    }
    return STICKY_POPUP_TYPES.includes(notification.type) ? 0 : POPUP_AUTO_HIDE_MS;
}

export type NotificationEntry = {
    /** Newest notification of the group, shown in the row */
    notification: NotificationLike;
    /** All loaded notifications of the group (incl. the shown one) */
    members: NotificationLike[];
    groupKey?: string;
};

/** Collapses repeated overdue/due notifications of the same to-do into one entry (list stays sorted) */
export function collapseRepeated(notifications: NotificationLike[]): NotificationEntry[] {
    const entries: NotificationEntry[] = [];
    const byKey = new Map<string, NotificationEntry>();
    for (const notification of notifications) {
        const key = REPEATED_GROUP_TYPES.includes(notification.type) ? notificationGroupKey(notification) : undefined;
        const existing = key ? byKey.get(key) : undefined;
        if (existing) {
            existing.members.push(notification);
            continue;
        }
        const entry: NotificationEntry = {notification, members: [notification], groupKey: key};
        entries.push(entry);
        if (key) {
            byKey.set(key, entry);
        }
    }
    return entries;
}
